import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
    ArrowLeft,
    Plus,
    XCircle
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { TextArea } from '@/components/base/textarea/textarea';
import { RadioGroup, RadioButton } from '@/components/base/radio-buttons/radio-buttons';
import { containerService } from '@/data/mock-containers';
import { collaborationService } from '@/data/mock-collaborations';
import { accountService } from '@/data/mock-accounts';
import { orderService } from '@/data/mock-orders';
import type { ContainerStatus } from '@/types/container';
import { useTranslation } from 'react-i18next';

interface ReceiverOption {
    id: string;
    label: string;
    email: string;
    country: string;
}

export const ContainerFormPage = () => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [availableReceivers, setAvailableReceivers] = useState<any[]>([]);
    const [selectedReceiver, setSelectedReceiver] = useState<any>(null);
    const [inviteNotes, setInviteNotes] = useState('');
    const [receiverOptions, setReceiverOptions] = useState<ReceiverOption[]>([]);
    const [orderOptions, setOrderOptions] = useState<Array<{ id: string; label: string }>>([]);

    const [formData, setFormData] = useState({
        containerNumber: '',
        routeFrom: '',
        routeTo: '',
        eta: '',
        valueAmount: '',
        valueCurrency: 'USD',
        status: 'In Transit' as ContainerStatus,
        receiverId: '',
        shippingCompany: '',
        orderIdMode: 'select' as 'select' | 'manual',
        orderId: ''
    });

    const statusOptions = [
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) }
    ];

    const currencyOptions = [
        { id: 'USD', label: 'USD' },
        { id: 'EUR', label: 'EUR' },
        { id: 'GBP', label: 'GBP' },
        { id: 'CHF', label: 'CHF' }
    ];

    // Pre-populate form from query params
    useEffect(() => {
        const routeFrom = searchParams.get('routeFrom');
        const routeTo = searchParams.get('routeTo');
        const shippingCompany = searchParams.get('shippingCompany');
        const commodity = searchParams.get('commodity');

        if (routeFrom || routeTo || shippingCompany) {
            setFormData(prev => ({
                ...prev,
                routeFrom: routeFrom || prev.routeFrom,
                routeTo: routeTo || prev.routeTo,
                shippingCompany: shippingCompany || prev.shippingCompany,
            }));
        }

        // If commodity is provided, add it to notes (only once)
        if (commodity) {
            setInviteNotes(prev => {
                // Only add if not already present
                if (!prev.includes(commodity)) {
                    return prev ? `${prev}\nCommodity: ${commodity}` : `Commodity: ${commodity}`;
                }
                return prev;
            });
        }
    }, [searchParams]);

    useEffect(() => {
        const loadReceivers = async () => {
            const accountId = localStorage.getItem('accountId') || '1';

            // Load active collaborations
            const collaborations = collaborationService.getByShipperId(accountId);
            const activeCollaborations = collaborations.filter(c => c.status === 'Active');

            // Load receiver accounts
            const allAccounts = accountService.getAll();
            const receiverAccounts = allAccounts.filter(acc => acc.userType === 'RECEIVER');

            // Combine data
            const options: ReceiverOption[] = activeCollaborations.map(collab => {
                const receiverAccount = receiverAccounts.find(acc => acc.id === collab.receiverId);
                if (!receiverAccount) return null;

                return {
                    id: receiverAccount.id,
                    label: `${receiverAccount.legalName} (${receiverAccount.country})`,
                    email: receiverAccount.email,
                    country: receiverAccount.country
                };
            }).filter(Boolean) as ReceiverOption[];

            setReceiverOptions(options);

            // Load available receivers for invitation
            const collaboratedReceiverIds = collaborations.map(c => c.receiverId);
            const available = receiverAccounts.filter(acc => !collaboratedReceiverIds.includes(acc.id));
            setAvailableReceivers(available);
        };

        loadReceivers();
    }, []);

    useEffect(() => {
        const loadOrders = () => {
            const accountId = localStorage.getItem('accountId') || '1';
            const orders = orderService.getByShipperId(accountId);
            const options = orders.map(order => ({
                id: order.id,
                label: `Order #${order.id}`
            }));
            setOrderOptions(options);
        };

        loadOrders();
    }, []);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleOrderIdModeChange = (mode: 'select' | 'manual') => {
        setFormData(prev => ({ ...prev, orderIdMode: mode, orderId: '' }));
    };

    const handleInviteReceiver = () => {
        if (!selectedReceiver) return;

        const accountId = localStorage.getItem('accountId') || '1';
        const newCollaboration = collaborationService.create({
            shipperId: accountId,
            receiverId: selectedReceiver.id,
            notes: inviteNotes
        });

        if (newCollaboration) {
            // Reload receivers
            window.location.reload();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Validation
            if (!formData.containerNumber || !formData.routeFrom || !formData.routeTo ||
                !formData.eta || !formData.valueAmount || !formData.receiverId) {
                setError(t('form.errors.allFieldsRequired', { ns: 'common' }));
                setIsLoading(false);
                return;
            }

            const valueAmount = parseFloat(formData.valueAmount);
            if (isNaN(valueAmount) || valueAmount <= 0) {
                setError(t('form.errors.amountInvalid', { ns: 'common' }));
                setIsLoading(false);
                return;
            }

            // Check if container number already exists
            const existingContainer = containerService.getAll().find(c =>
                c.containerNumber.toLowerCase() === formData.containerNumber.toLowerCase()
            );
            if (existingContainer) {
                setError(t('form.errors.containerExists', { ns: 'pages:containers' }));
                setIsLoading(false);
                return;
            }

            // Create container
            const accountId = localStorage.getItem('accountId') || '1';
            const newContainer = containerService.create({
                shipperId: accountId,
                receiverId: formData.receiverId,
                containerNumber: formData.containerNumber,
                route: {
                    from: formData.routeFrom,
                    to: formData.routeTo
                },
                progress: 0,
                eta: new Date(formData.eta),
                documentStatus: 'Pending',
                riskLevel: 'Low',
                value: {
                    amount: valueAmount,
                    currency: formData.valueCurrency
                },
                status: formData.status,
                shippingCompany: formData.shippingCompany || undefined,
                // Fallback commodity finché non aggiungiamo il campo nel form
                commodity: 'electronics',
                orderId: formData.orderId || undefined
            });

            if (newContainer) {
                navigate(`/shipper/containers/${newContainer.id}`);
            } else {
                setError(t('form.errors.generic'));
            }
        } catch (err) {
            setError(t('form.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/shipper/containers')}
                        className="text-tertiary hover:text-primary"
                    >
                        <ArrowLeft className="size-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('form.title')}</h1>
                        <p className="text-tertiary mt-1">
                            {t('form.subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Error Message */}
                {error && (
                    <div className="rounded-lg bg-error-50 p-4 text-sm text-error-700">
                        {error}
                    </div>
                )}

                {/* Informazioni Container */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.containerInfo')}</h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Input
                            label={t('form.fields.containerNumber')}
                            placeholder={t('form.fields.containerNumberPlaceholder')}
                            value={formData.containerNumber}
                            onChange={(value) => handleInputChange('containerNumber', value)}
                            isRequired
                            size="md"
                        />
                        <Select
                            label={t('form.fields.status')}
                            placeholder={t('form.fields.statusPlaceholder')}
                            items={statusOptions}
                            selectedKey={formData.status}
                            onSelectionChange={(key) => handleInputChange('status', key as string)}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Route */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.route')}</h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Input
                            label={t('form.fields.routeFrom')}
                            placeholder={t('form.fields.routeFromPlaceholder')}
                            value={formData.routeFrom}
                            onChange={(value) => handleInputChange('routeFrom', value)}
                            isRequired
                            size="md"
                        />
                        <Input
                            label={t('form.fields.routeTo')}
                            placeholder={t('form.fields.routeToPlaceholder')}
                            value={formData.routeTo}
                            onChange={(value) => handleInputChange('routeTo', value)}
                            isRequired
                            size="md"
                        />
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Input
                            label={t('form.fields.eta')}
                            type="date"
                            value={formData.eta}
                            onChange={(value) => handleInputChange('eta', value)}
                            isRequired
                            size="md"
                        />
                        <Select
                            label={t('form.fields.shippingCompany')}
                            placeholder={t('form.fields.shippingCompanyPlaceholder')}
                            items={[
                                { id: 'Maersk', label: 'Maersk' },
                                { id: 'MSC', label: 'MSC' },
                                { id: 'CMA CGM', label: 'CMA CGM' },
                                { id: 'Hapag Lloyd', label: 'Hapag Lloyd' },
                                { id: 'ONE', label: 'ONE' },
                            ]}
                            selectedKey={formData.shippingCompany}
                            onSelectionChange={(key) => handleInputChange('shippingCompany', key as string)}
                            isRequired
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Order ID */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.order')}</h2>
                    <div className="space-y-6">
                        <RadioGroup
                            value={formData.orderIdMode}
                            onChange={(value) => handleOrderIdModeChange(value as 'select' | 'manual')}
                            size="md"
                        >
                            <RadioButton value="select" label={t('form.fields.orderIdSelectExisting')} />
                            <RadioButton value="manual" label={t('form.fields.orderIdEnterManually')} />
                        </RadioGroup>

                        {formData.orderIdMode === 'select' && (
                            <Select
                                label={t('form.fields.orderId')}
                                placeholder={t('form.fields.orderIdPlaceholder')}
                                items={orderOptions}
                                selectedKey={formData.orderId}
                                onSelectionChange={(key) => handleInputChange('orderId', key as string)}
                                size="md"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        )}

                        {formData.orderIdMode === 'manual' && (
                            <Input
                                label={t('form.fields.orderId')}
                                placeholder={t('form.fields.orderIdPlaceholder')}
                                value={formData.orderId}
                                onChange={(value) => handleInputChange('orderId', value)}
                                size="md"
                            />
                        )}
                    </div>
                </div>

                {/* Valore */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.value')}</h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <Input
                            label={t('form.fields.amount')}
                            type="number"
                            placeholder={t('form.fields.amountPlaceholder')}
                            value={formData.valueAmount}
                            onChange={(value) => handleInputChange('valueAmount', value)}
                            isRequired
                            size="md"
                        />
                        <Select
                            label={t('form.fields.currency')}
                            placeholder={t('form.fields.currencyPlaceholder')}
                            items={currencyOptions}
                            selectedKey={formData.valueCurrency}
                            onSelectionChange={(key) => handleInputChange('valueCurrency', key as string)}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Receiver */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold text-primary">{t('form.sections.receiver')}</h2>
                        <Button
                            type="button"
                            onClick={() => setShowInviteModal(true)}
                            size="sm"
                            color="secondary"
                            className="inline-flex items-center gap-2"
                            iconLeading={<Plus className="size-4" />}
                        >
                            {t('list.invite', { ns: 'pages:receivers' })}
                        </Button>
                    </div>
                    <div>
                        <Select
                            label={t('form.fields.receiver')}
                            placeholder={t('form.fields.receiverPlaceholder')}
                            items={receiverOptions}
                            selectedKey={formData.receiverId}
                            onSelectionChange={(key) => handleInputChange('receiverId', key as string)}
                            isRequired
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    <div className="flex items-center justify-between w-full">
                                        <span>{item.label}</span>
                                        <Badge color="success" size="sm">{t('status.active', { ns: 'types' })}</Badge>
                                    </div>
                                </Select.Item>
                            )}
                        </Select>
                        {receiverOptions.length === 0 && (
                            <p className="text-sm text-tertiary mt-2">
                                {t('form.noReceivers', { ns: 'pages:containers' })}
                            </p>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={() => navigate('/shipper/containers')}
                        color="secondary"
                        size="lg"
                    >
                        {t('form.actions.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        size="lg"
                        isLoading={isLoading}
                        disabled={!formData.containerNumber || !formData.routeFrom || !formData.routeTo ||
                            !formData.eta || !formData.valueAmount || !formData.receiverId}
                    >
                        {t('form.actions.create')}
                    </Button>
                </div>
            </form>

            {/* Invite Receiver Modal */}
            {showInviteModal && (
                <ModalOverlay isOpen={showInviteModal} onOpenChange={setShowInviteModal}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('list.invite', { ns: 'pages:receivers' })}</h2>
                                    <button
                                        onClick={() => setShowInviteModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('form.selectReceiver', { ns: 'pages:containers' })}
                                        </label>
                                        <Select
                                            placeholder={t('form.fields.receiverPlaceholder')}
                                            items={availableReceivers.map(rec => ({ id: rec.id, label: `${rec.legalName} (${rec.country})` }))}
                                            selectedKey={selectedReceiver?.id || ''}
                                            onSelectionChange={(key) => {
                                                const receiver = availableReceivers.find(r => r.id === key);
                                                setSelectedReceiver(receiver);
                                            }}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <div>
                                        <TextArea
                                            label={t('form.notesOptional', { ns: 'pages:containers' })}
                                            placeholder={t('form.notesPlaceholder', { ns: 'pages:containers' })}
                                            value={inviteNotes}
                                            onChange={setInviteNotes}
                                        />
                                    </div>

                                    {selectedReceiver && (
                                        <div className="rounded-lg bg-secondary p-4">
                                            <h4 className="text-sm font-medium text-primary mb-2">{t('form.selectedReceiver', { ns: 'pages:containers' })}</h4>
                                            <div className="text-sm text-tertiary">
                                                <div><strong>{t('form.name', { ns: 'pages:containers' })}:</strong> {selectedReceiver.legalName}</div>
                                                <div><strong>{t('form.email', { ns: 'pages:containers' })}:</strong> {selectedReceiver.email}</div>
                                                <div><strong>{t('form.country', { ns: 'pages:containers' })}:</strong> {selectedReceiver.country}</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-end gap-3">
                                        <Button
                                            onClick={() => setShowInviteModal(false)}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('form.actions.cancel')}
                                        </Button>
                                        <Button
                                            onClick={handleInviteReceiver}
                                            disabled={!selectedReceiver}
                                            color="primary"
                                            size="md"
                                        >
                                            {t('form.sendInvite', { ns: 'pages:containers' })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};
